import { useCallback } from 'react';

import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../shared/api/client';
import { isNetworkError, logError, validateAccountInfo, validateApiResponse, validatePositionArray, WS_CHANNELS } from '../shared/config/constants';
import { QK } from '../shared/model/queryKeys';
import { useBusChannel, useWebSocketStatus } from '../shared/ws/useBusSocket';

type Numeric = number | string;

interface Balance {
  asset: string;
  free?: Numeric;
  total?: Numeric;
}

interface AccountPosition {
  unrealizedPnl?: Numeric;
  unrealized_pnl?: Numeric;
  [key: string]: unknown;
}

interface AccountInfo {
  balances: Balance[];
  totalWalletBalance?: Numeric;
  availableBalance?: Numeric;
  positions?: AccountPosition[];
  [key: string]: unknown;
}

export interface Position {
  symbol: string;
  position_side: string;
  positionSide?: string;
  quantity?: number;
  positionAmt?: number;
  mark_price?: number;
  markPrice?: number;
  current_price?: number;
  unrealized_pnl?: number;
  unrealizedPnl?: number;
  [key: string]: unknown;
}

export interface PortfolioViewModel extends AccountInfo {
  balance: number;
  availableBalance: number;
  unrealizedPnL: number;
  marginUsed: number;
}

function toNumber(x: unknown, fallback = 0): number {
  const n = typeof x === 'number' ? x : Number(x);
  return Number.isFinite(n) ? n : fallback;
}

type UnknownRecord = Record<string, unknown>;

const isRecord = (v: unknown): v is UnknownRecord => 
  !!v && typeof v === 'object' && !Array.isArray(v);

const readField = (record: UnknownRecord, ...keys: string[]): unknown => {
  for (const key of keys) {
    if (record[key] !== undefined) return record[key];
  }
  return undefined;
};

const normalizePosition = (value: unknown): Position | null => {
  if (!isRecord(value)) return null;
  const symbol = String(readField(value, 'symbol') ?? '');
  const positionSide = String(readField(value, 'position_side', 'positionSide', 'side') ?? '').toUpperCase();
  if (!symbol || !positionSide) return null;

  const quantity = toNumber(readField(value, 'quantity', 'position_amt', 'positionAmt'), 0);
  const markPrice = toNumber(readField(value, 'mark_price', 'markPrice', 'current_price', 'currentPrice'), 0);
  const unrealizedPnl = toNumber(readField(value, 'unrealized_pnl', 'unrealizedPnl'), 0);

  return {
    ...value,
    symbol,
    position_side: positionSide,
    positionSide,
    quantity,
    positionAmt: quantity,
    mark_price: markPrice,
    markPrice,
    current_price: markPrice,
    unrealized_pnl: unrealizedPnl,
    unrealizedPnl,
  };
};

const sumUnrealizedPnl = (positions: AccountPosition[]): number =>
  positions.reduce(
    (acc, position) => acc + toNumber(position?.unrealizedPnl ?? position?.unrealized_pnl, 0),
    0,
  );

export const usePortfolio = (agentId: string) => {
  const queryClient = useQueryClient();

  // Отслеживание доступности WebSocket для fallback
  const { isConnected: isWsConnected } = useWebSocketStatus();

  const wsEnabled = !!agentId;

  // WebSocket подписка на обновления портфолио
  useBusChannel(
    agentId ? WS_CHANNELS.PORTFOLIO(agentId) : '',
    useCallback((event: unknown) => {
      try {
        const payload = isRecord(event) ? (event as { payload?: unknown }).payload : event;
        if (payload && isRecord(payload)) {
          queryClient.setQueryData(QK.portfolio(agentId), (oldData: PortfolioViewModel | undefined) => {
            if (!oldData) return oldData;
            const accountInfo = payload as Partial<AccountInfo>;
            const balances = Array.isArray(accountInfo.balances) ? accountInfo.balances : oldData.balances;
            const usdt = balances.find((b) => String(b.asset || '').toUpperCase() === 'USDT');
            const totalWalletBalance = toNumber(accountInfo.totalWalletBalance ?? usdt?.total ?? oldData.balance, 0);
            const availableBalance = toNumber(accountInfo.availableBalance ?? usdt?.free ?? oldData.availableBalance, 0);
            const positions = Array.isArray(accountInfo.positions) ? accountInfo.positions : oldData.positions || [];
            const totalUnrealizedPnl = sumUnrealizedPnl(positions);
            const marginUsed = Math.max(0, totalWalletBalance - availableBalance);

            return {
              ...oldData,
              ...accountInfo,
              balance: totalWalletBalance,
              availableBalance,
              unrealizedPnL: totalUnrealizedPnl,
              marginUsed,
            };
          });
        }
      } catch (error) {
        logError('Failed to process portfolio WebSocket update', error);
        if (!isWsConnected) {
          queryClient.invalidateQueries({ queryKey: QK.portfolio(agentId) });
        }
      }
    }, [agentId, queryClient, isWsConnected]),
    { enabled: wsEnabled, snapshot: true }
  );

  // WebSocket подписка на обновления позиций
  useBusChannel(
    agentId ? WS_CHANNELS.POSITIONS(agentId) : '',
    useCallback((event: unknown) => {
      try {
        const payload = isRecord(event) ? (event as { payload?: unknown }).payload : event;
        if (payload) {
          const positionsPayload = Array.isArray(payload) 
            ? payload 
            : (isRecord(payload) && Array.isArray((payload as UnknownRecord).positions) 
              ? (payload as UnknownRecord).positions as Position[]
              : []);
          const positions = Array.isArray(positionsPayload)
            ? positionsPayload.map((position) => normalizePosition(position)).filter((position): position is Position => position !== null)
            : [];
          if (validateApiResponse<Position[]>(positions, validatePositionArray)) {
            queryClient.setQueryData(QK.positions(agentId), positions);
          }
        }
      } catch (error) {
        logError('Failed to process positions WebSocket update', error);
        if (!isWsConnected) {
          queryClient.invalidateQueries({ queryKey: QK.positions(agentId) });
        }
      }
    }, [agentId, queryClient, isWsConnected]),
    { enabled: wsEnabled, snapshot: true }
  );

  // Fetch portfolio data
  const portfolioQuery = useQuery<PortfolioViewModel, Error>({
    queryKey: QK.portfolio(agentId),
    enabled: !!agentId,
    queryFn: async () => {
      const safeId = encodeURIComponent(agentId);

      const response = await api.get<unknown>(`/api/brokers/${safeId}/account`);
      const data: unknown = response?.data ?? response;

      // Validate API response structure
      if (!validateApiResponse<AccountInfo>(data, validateAccountInfo, 'Invalid portfolio API response: expected AccountInfo object')) {
        throw new Error('Invalid portfolio API response: expected AccountInfo object');
      }

      // Обогащаем агрегатами для удобства UI
      const a: AccountInfo = data;
      const balances = Array.isArray(a.balances) ? a.balances : [];
      const usdt = balances.find((b) => String(b.asset || '').toUpperCase() === 'USDT');
      const totalWalletBalance = toNumber(a.totalWalletBalance ?? usdt?.total, 0);
      const availableBalance = toNumber(a.availableBalance ?? usdt?.free, 0);
      const positions = Array.isArray(a.positions) ? a.positions : [];
      const totalUnrealizedPnl = sumUnrealizedPnl(positions);
      const marginUsed = Math.max(0, totalWalletBalance - availableBalance);

      return {
        ...a,
        balance: totalWalletBalance,
        availableBalance,
        unrealizedPnL: totalUnrealizedPnl,
        marginUsed,
      };
    },
    // Используем polling только если WebSocket недоступен
    refetchInterval: isWsConnected ? false : 10000,
    refetchIntervalInBackground: true,
    placeholderData: keepPreviousData,
    staleTime: 15000,
    retry: (failureCount, error: unknown) => {
      if (isNetworkError(error)) return false;
      const status =
        isRecord(error) && isRecord(error.response) ? Number((error.response as UnknownRecord).status) : undefined;
      if (status === 404) return false;
      return failureCount < 3;
    }
  });

  // Fetch positions
  const positionsQuery = useQuery<Position[], Error>({
    queryKey: QK.positions(agentId),
    enabled: !!agentId,
    retry: (failureCount, error: unknown) => {
      if (isNetworkError(error)) return false;
      const status =
        isRecord(error) && isRecord(error.response) ? Number((error.response as UnknownRecord).status) : undefined;
      if (status === 404) return false;
      return failureCount < 3;
    },
    queryFn: async () => {
      const response = await api.get<unknown>(`/api/brokers/${encodeURIComponent(agentId)}/positions`);
      const payload: unknown = response?.data ?? response;

      // Validate API response
      const normalized = Array.isArray(payload)
        ? payload.map((position) => normalizePosition(position)).filter((position): position is Position => position !== null)
        : [];
      if (!validateApiResponse<Position[]>(normalized, validatePositionArray, 'Invalid positions response format')) {
        return [];
      }

      return normalized;
    },
    // Используем polling только если WebSocket недоступен
    refetchInterval: isWsConnected ? false : 10000,
    refetchIntervalInBackground: true,
    placeholderData: keepPreviousData,
    staleTime: 15000,
  });

  // Сводку статистики (/api/stats/summary) хук не опрашивает: её никто в раскладке не
  // показывал, а расчёт по всей истории занимает event loop API ~0.7 с — раз в минуту на
  // каждую открытую вкладку. Сводку грузит страница «Статистика», когда её открывают.

  // 404 обработка вынесена в onError колбэки запросов

  return {
    portfolio: portfolioQuery.data,
    positions: positionsQuery.data,
    isLoading: portfolioQuery.isLoading || positionsQuery.isLoading,
    error: portfolioQuery.error || positionsQuery.error,
    refetch: () => {
      portfolioQuery.refetch();
      positionsQuery.refetch();
      return Promise.resolve();
    },
  };
};
